/**
 * D854 (L7a-027): the ride 360 prints versions and multipliers as words, not as the stored labels. Pure helpers (the screen
 * adds the names): a governed-policy version label (`fare.finalRevision@market:br:paracatu#v3`), a tariff version
 * (`br:paracatu-go-illustrative-v1`) and a surge multiplier in basis points (`10000` -> "1,00x").
 */

export type PolicyVersionOrigin = 'country' | 'region' | 'market' | 'default' | 'invalid';

export interface PolicyVersionParts {
  /** The governed setting key (`fare.finalRevision`): the screen names it through the `settings.key` vocabulary. */
  readonly key: string;
  readonly origin: PolicyVersionOrigin;
  /** The country code, state code or market id the version was set at (absent for `default` / `invalid`). */
  readonly ref?: string;
  readonly version?: number;
}

const POLICY_VERSION = /^([A-Za-z][A-Za-z0-9_.-]*)@(?:(country|region|market):(.+)#v(\d+)|(default|invalid))$/;

/** Reads `key@scope:ref#vN` (or `key@default`); undefined for any other label, which the screen then prints as it is. */
export function parsePolicyVersion(raw: string | undefined): PolicyVersionParts | undefined {
  if (raw === undefined) return undefined;
  const m = POLICY_VERSION.exec(raw.trim());
  if (m === null) return undefined;
  const key = m[1] as string;
  if (m[5] !== undefined) return { key, origin: m[5] as 'default' | 'invalid' };
  return { key, origin: m[2] as 'country' | 'region' | 'market', ref: m[3] as string, version: Number(m[4]) };
}

/** The version number a tariff version label ends with (`...-v1` -> 1); undefined when it does not end with one. */
export function tariffVersionNumber(raw: string | undefined): number | undefined {
  const m = /-v(\d+)$/.exec((raw ?? '').trim());
  return m === null ? undefined : Number(m[1]);
}

/** A surge multiplier given in basis points as a number with two decimals and an "x" (`10000` -> "1,00x" in pt, "1.00x" in en). */
export function formatMultiplier(basisPoints: number, locale: string): string {
  if (!Number.isFinite(basisPoints)) return '—';
  return `${new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(basisPoints / 10_000)}x`;
}
